import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Package, ClipboardList, Bell, Users, ScrollText,
  Boxes, LogOut, Menu, X, Sparkles, Send, Settings2, HardDrive,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ROLE_LABELS = {
  super_admin: "Super Admin", store_manager: "Store Manager",
  procurement_officer: "Procurement Officer", quality_officer: "Quality Officer",
  technician: "Technician", center_staff: "Center Staff", field_staff: "Field Staff",
};

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: "all" },
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory",
    roles: ["super_admin", "store_manager", "procurement_officer", "quality_officer", "center_staff", "field_staff"] },
  { to: "/items", label: "Item Master", icon: Package, testid: "nav-items",
    roles: ["super_admin", "store_manager", "procurement_officer"] },
  { to: "/assets", label: "Asset Register", icon: HardDrive, testid: "nav-assets",
    roles: ["super_admin", "store_manager", "procurement_officer"] },
  { to: "/consumption", label: "Consumption", icon: ClipboardList, testid: "nav-consumption",
    roles: ["super_admin", "technician", "store_manager"] },
  { to: "/requisitions", label: "Requisitions", icon: Send, testid: "nav-requisitions",
    roles: ["super_admin", "technician", "center_staff", "field_staff", "store_manager", "procurement_officer", "quality_officer"] },
  { to: "/transactions", label: "Stock Ledger", icon: ScrollText, testid: "nav-transactions",
    roles: ["super_admin", "store_manager", "procurement_officer", "quality_officer"] },
  { to: "/assistant", label: "AI Assistant", icon: Sparkles, testid: "nav-assistant", roles: "all" },
  { to: "/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts", roles: "all" },
  { to: "/users", label: "User Management", icon: Users, testid: "nav-users", roles: ["super_admin"] },
  { to: "/masters", label: "Masters", icon: Settings2, testid: "nav-masters", roles: ["super_admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const items = NAV.filter((n) => n.roles === "all" || n.roles.includes(user.role));

  const SidebarInner = () => (
    <div className="flex flex-col h-full" style={{ background: "#042C53" }}>
      <div className="px-5 py-5 border-b border-white/10">
        <Logo dark />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive ? "text-white" : "text-white/70 hover:text-white hover:bg-white/5"
              }`
            }
            style={({ isActive }) => (isActive ? { background: "#0a8f8f" } : {})}
          >
            <n.icon size={18} strokeWidth={2} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-2 py-2 mb-1">
          <div className="text-white text-sm font-semibold truncate">{user.name}</div>
          <div className="text-white/50 text-xs">{ROLE_LABELS[user.role]}</div>
          {user.location_name && <div className="text-white/40 text-xs">{user.location_name}</div>}
        </div>
        <Button
          data-testid="logout-button"
          onClick={logout}
          variant="ghost"
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
        >
          <LogOut size={18} className="mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-30">
        <SidebarInner />
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* top header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 h-16 flex items-center px-4 lg:px-8 justify-between">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-navy"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
            >
              {open ? <X /> : <Menu />}
            </button>
            <div className="lg:hidden">
              <Logo />
            </div>
            <h1 className="hidden lg:block font-serif text-2xl tracking-tight text-navy">
              {items.find((i) => window.location.pathname === i.to)?.label || "Overview"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium px-3 py-1.5 rounded-full text-white" style={{ background: "#0a8f8f" }}>
              {ROLE_LABELS[user.role]}
            </span>
          </div>
        </header>

        <main className="p-4 lg:p-8 fade-in">{children}</main>
      </div>
    </div>
  );
}
